/**
 * Singleton manager for free gift pairings and add-to-cart interception
 */
class TdFreeGiftManager {
  static pairings = [];
  static isProcessingGift = false;
  static interceptorInstalled = false;
  static userRemovedGifts = new Set(); // Track gifts user manually removed
  
  // TEMPORARY: Track recent add-to-cart requests to prevent duplicates
  // This is a workaround for a vendor bug where duplicate fetch requests are made
  // See duplicate prevention block below for full details
  // TODO: Remove this Map once the vendor bug is fixed
  static recentAdds = new Map();

  /**
   * Register a free gift pairing from a td-free-gift element
   */
  static registerPairing(element) {
    const pairing = {
      productId: element.productId,
      freeGiftVariantId: element.freeGiftVariantId,
      requiresSellingPlan: element.requiresSellingPlan,
    };
    
    this.pairings.push(pairing);

    // Install fetch interceptor once
    if (!this.interceptorInstalled) {
      this.installFetchInterceptor();
      this.interceptorInstalled = true;
    }
  }

  /**
   * Patch window.fetch to intercept add-to-cart requests
   */
  static installFetchInterceptor() {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const [resource, config] = args;
      const url = typeof resource === "string" ? resource : resource.url;

      // Check for cart add requests
      const isCartAdd =
        config?.method === "POST" &&
        (url.includes("/cart/add") || url.includes(window.theme?.routes?.cart_add_url));

      // Check for cart change/update requests
      const isCartChange =
        config?.method === "POST" &&
        (url.includes("/cart/change") || url.includes(window.theme?.routes?.cart_change_url));

      // Pass through if not a cart operation
      if (!isCartAdd && !isCartChange) {
        return originalFetch(...args);
      }

      // Handle cart add
      if (isCartAdd) {
        const requestId = Math.random().toString(36).substring(2, 11);

        // Check if this is our own gift addition (prevent recursion)
        if (config?.headers?.["X-TD-Free-Gift"]) {
          return originalFetch(...args);
        }

        try {
          // ========================================================================
          // TEMPORARY: DUPLICATE REQUEST PREVENTION
          // ========================================================================
          // ISSUE: Multiple third-party scripts wrap window.fetch and one of them
          // is causing duplicate add-to-cart requests (1-2ms apart). This results
          // in products being added to cart with quantity 2 instead of 1.
          //
          // CULPRITS: Based on stack traces, the duplicate originates from one of:
          //   - Recharge (storefront-experiences.js)
          //   - WPM/Wunderkind (tracking script)
          //   - Swym (wishlist)
          //   - Shopify's shop_events_listener (analytics)
          //
          // WORKAROUND: We detect duplicate requests by tracking variant+selling_plan
          // combinations and block any duplicate within 500ms. This prevents the
          // duplicate from reaching Shopify's cart API.
          //
          // TODO: This is a TEMPORARY fix. Long-term solutions:
          //   1. Identify which vendor script is causing the duplicate
          //   2. Contact that vendor to fix their fetch wrapper
          //   3. Once fixed, REMOVE this entire duplicate detection block
          //   4. Consider reducing the number of fetch wrappers in the stack
          //
          // TESTING: To verify this is still needed, temporarily disable this block
          // and test adding subscription products - they should add with quantity 2
          // if the vendor bug still exists.
          // ========================================================================
          
          // Try to extract variant info from the request body to check for duplicates
          let variantIdFromRequest = null;
          let sellingPlanIdFromRequest = null;
          
          if (config?.body instanceof FormData) {
            variantIdFromRequest = config.body.get('id');
            sellingPlanIdFromRequest = config.body.get('selling_plan');
          } else if (typeof config?.body === 'string') {
            try {
              const bodyData = JSON.parse(config.body);
              variantIdFromRequest = bodyData.id;
              sellingPlanIdFromRequest = bodyData.selling_plan;
            } catch (e) {
              // Not JSON, might be URLSearchParams
            }
          }
          
          if (variantIdFromRequest) {
            const addKey = `${variantIdFromRequest}-${sellingPlanIdFromRequest || 'none'}`;
            const now = Date.now();
            
            // Check if we just processed this exact same add within 500ms
            if (this.recentAdds.has(addKey)) {
              const lastAdd = this.recentAdds.get(addKey);
              const timeDiff = now - lastAdd;
              if (timeDiff < 500) {
                // Return a mock success response to prevent the duplicate from reaching Shopify
                // The first request will complete normally, this duplicate is simply ignored
                return new Response(JSON.stringify({ 
                  blocked: true, 
                  message: 'Duplicate request prevented by TD Free Gift',
                  note: 'This is expected behavior - a vendor script is making duplicate fetch calls'
                }), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' }
                });
              }
            }
            
            // Track this add BEFORE executing the fetch
            this.recentAdds.set(addKey, now);
            
            // Clean up old entries (older than 2 seconds)
            for (const [key, timestamp] of this.recentAdds.entries()) {
              if (now - timestamp > 2000) {
                this.recentAdds.delete(key);
              }
            }
          }
          
          // ========================================================================
          // END TEMPORARY DUPLICATE PREVENTION
          // ========================================================================
          
          // Execute the original add-to-cart request
          const response = await originalFetch(...args);

          // Only process successful responses
          if (response.ok) {
            // Extract the request body to determine what was added
            this.processAddToCart(config?.body, response.clone(), requestId);
          }

          return response;
        } catch (error) {
          console.error(`[TD Free Gift] Error intercepting fetch:`, error);
          throw error;
        }
      }

      // Handle cart change
      if (isCartChange) {
        // Check if this is our own gift removal (prevent recursion)
        if (config?.headers?.["X-TD-Free-Gift-Remove"]) {
          return originalFetch(...args);
        }

        try {
          // Check if user is removing a gift before the request
          const requestData = config?.body ? JSON.parse(config.body) : null;
          
          if (requestData?.quantity === 0 || requestData?.quantity === "0") {
            // Check if this is a gift being removed
            const itemKey = requestData.id || requestData.line;
            await this.checkIfUserRemovingGift(itemKey);
          }

          // Execute the original cart change request
          const response = await originalFetch(...args);

          // Only process successful responses
          if (response.ok) {
            // Clone response so we can read it without consuming the original
            const clonedResponse = response.clone();

            // Check if we need to add or remove any gifts
            this.processCartChange(clonedResponse);
          }

          return response;
        } catch (error) {
          console.error("[TD Free Gift] Error intercepting cart change:", error);
          throw error;
        }
      }
    };
  }

  /**
   * Process an add-to-cart request and check if a free gift should be added
   */
  static async processAddToCart(requestBody, response, requestId = 'unknown') {
    try {
      // Prevent concurrent gift processing
      if (this.isProcessingGift) {
        return;
      }

      // Parse the request body to extract product info
      let addedProductId = null;
      let hasSellingPlan = false;

      if (requestBody) {
        // Get product ID from response
        const responseData = await response.json();
        
        if (responseData?.product_id) {
          addedProductId = responseData.product_id.toString();
        }
        
        // Check response for selling_plan_allocation (most reliable method)
        if (responseData?.selling_plan_allocation) {
          hasSellingPlan = true;
        }
      }

      if (!addedProductId) {
        return;
      }

      // Find matching pairing
      const matchingPairing = this.pairings.find((pairing) => {
        const productMatches = pairing.productId === addedProductId;
        
        // If requiresSellingPlan is false, match ANY purchase (one-time OR subscription)
        // If requiresSellingPlan is true, only match if item HAS a selling plan
        const sellingPlanMatches = pairing.requiresSellingPlan ? hasSellingPlan : true;
        
        return productMatches && sellingPlanMatches;
      });

      if (!matchingPairing) {
        return;
      }

      // Check if gift is already in cart
      const isGiftInCart = await this.isGiftInCart(matchingPairing.freeGiftVariantId);
      
      if (isGiftInCart) {
        return;
      }

      // Add the free gift
      await this.addFreeGift(matchingPairing.freeGiftVariantId, requestId);
    } catch (error) {
      console.error("[TD Free Gift] Error processing add-to-cart:", error);
    }
  }

  /**
   * Check if the gift variant is already in the cart AS A GIFT line.
   *
   * The `_td_gift: "1"` property filter is critical: customers may have the same
   * variant in their cart as a paid purchase. We only want to detect (and later
   * remove) lines we added as gifts, never paid lines that happen to share the
   * variant id.
   */
  static async isGiftInCart(variantId) {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      return cart.items.some((item) => {
        return (
          item.variant_id.toString() === variantId.toString() &&
          item.properties &&
          item.properties._td_gift === "1"
        );
      });
    } catch (error) {
      console.error("[TD Free Gift] Error checking cart:", error);
      return false;
    }
  }

  /**
   * Check if the user is manually removing a gift (before cart change happens)
   */
  static async checkIfUserRemovingGift(lineOrKey) {
    try {
      // Get current cart to check if this item is a gift (BEFORE the removal happens)
      const response = await fetch("/cart.js");
      const cart = await response.json();
      
      // The lineOrKey could be either a line number (1-based index) or a key string
      let item;
      if (typeof lineOrKey === 'number') {
        // It's a line number (1-based), convert to 0-based index
        item = cart.items[lineOrKey - 1];
      } else {
        // It's a key string
        item = cart.items.find(i => i.key === lineOrKey);
      }
      
      if (item && item.properties?._td_gift === "1") {
        // User is manually removing a gift, track it
        const variantId = item.variant_id.toString();
        this.userRemovedGifts.add(variantId);
      }
    } catch (error) {
      console.error("[TD Free Gift] Error checking if user removing gift:", error);
    }
  }

  /**
   * Process a cart change request and check if free gifts should be added or removed.
   *
   * Pairings are grouped by free-gift variant so that multiple trigger products can
   * map to the same gift. The gift stays in the cart as long as ANY of its triggers
   * is in the cart, and is only removed when none are.
   */
  static async processCartChange(response) {
    try {
      // Get the updated cart state
      const cartData = await response.json();

      // Group pairings by free gift variant id
      const giftGroups = new Map();
      for (const pairing of this.pairings) {
        const list = giftGroups.get(pairing.freeGiftVariantId) || [];
        list.push(pairing);
        giftGroups.set(pairing.freeGiftVariantId, list);
      }

      for (const [giftVariantId, group] of giftGroups) {
        const anyTriggerInCart = cartData.items.some((item) => {
          return group.some((pairing) => {
            const productMatches = item.product_id.toString() === pairing.productId;
            const hasSellingPlan = !!item.selling_plan_allocation;
            const sellingPlanMatches = pairing.requiresSellingPlan ? hasSellingPlan : true;
            return productMatches && sellingPlanMatches && item.quantity > 0;
          });
        });

        // Only match gift lines (filter on the _td_gift property), never a paid
        // line that happens to share the variant id.
        const giftInCart = cartData.items.find((item) => {
          return (
            item.variant_id.toString() === giftVariantId.toString() &&
            item.properties &&
            item.properties._td_gift === "1"
          );
        });

        // Add gift if any trigger is present, gift is not yet in cart, and the user
        // hasn't manually removed it.
        if (anyTriggerInCart && !giftInCart) {
          if (!this.userRemovedGifts.has(giftVariantId.toString())) {
            await this.addFreeGift(giftVariantId);
          }
        }

        // Remove gift only if NO trigger is present and gift is in cart.
        if (!anyTriggerInCart && giftInCart) {
          await this.removeFreeGift(giftInCart.key);
        }
      }
    } catch (error) {
      console.error("[TD Free Gift] Error processing cart change:", error);
    }
  }

  /**
   * Add the free gift to the cart
   */
  static async addFreeGift(variantId, requestId = 'unknown') {
    this.isProcessingGift = true;

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-TD-Free-Gift": "1",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            _td_gift: "1",
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[TD Free Gift] Failed to add gift:", errorText);
        throw new Error(`Failed to add free gift: ${response.statusText}`);
      }
      
      // Clear this gift from the user-removed list since we just added it
      this.userRemovedGifts.delete(variantId.toString());

      // Trigger cart update events for theme compatibility
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
        })
      );

      // Additional event for drawer/cart updates
      if (window.theme?.settings?.cart_drawer) {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
          })
        );
      }
    } catch (error) {
      console.error("[TD Free Gift] Error adding free gift:", error);
    } finally {
      this.isProcessingGift = false;
    }
  }

  /**
   * Remove the free gift from the cart
   */
  static async removeFreeGift(itemKey) {
    try {
      const response = await fetch(window.theme?.routes?.cart_change_url || "/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-TD-Free-Gift-Remove": "1",
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[TD Free Gift] Failed to remove gift:", errorText);
        throw new Error(`Failed to remove free gift: ${response.statusText}`);
      }

      // Trigger cart update events for theme compatibility
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
        })
      );

      // Additional event for drawer/cart updates
      if (window.theme?.settings?.cart_drawer) {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
          })
        );
      }
    } catch (error) {
      console.error("[TD Free Gift] Error removing free gift:", error);
    }
  }
}

// Define web component after manager class
if (!customElements.get("td-free-gift")) {
  class TdFreeGift extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId;
      this.freeGiftVariantId = this.dataset.freeGiftVariantId;
      this.requiresSellingPlan = this.dataset.requiresSellingPlan === "true";
    }

    connectedCallback() {
      // Validate that we have required data
      if (!this.productId) {
        console.error("[TD Free Gift] Missing product ID, skipping pairing");
        return;
      }
      
      if (!this.freeGiftVariantId) {
        console.error("[TD Free Gift] Missing free gift variant ID, skipping pairing");
        return;
      }
      
      TdFreeGiftManager.registerPairing(this);
    }
  }

  customElements.define("td-free-gift", TdFreeGift);
}

